/**
 * 견적서 탭 — 목록 + 작성/수정/삭제 + 미리보기 진입.
 *
 * 컬럼: 견적일자, 담당, 수신, 프로젝트명, 상세내용(첫행+"외"), 공급가총계(VAT별도),
 *       영업진행 연동(Phase B 후속), Excel 다운로드, ✏ 수정, 🗑 삭제
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import { costSheetUrl } from '../../lib/cost-sheet-api';
import {
  createQuote,
  deleteQuote,
  getQuote,
  listQuotes,
  parseQuoteXlsx,
  quoteDetailToDraft,
  quoteExcelDownloadUrl,
  quotePdfDownloadUrl,
  updateQuote,
  type QuoteCompany,
  type QuoteKind,
  type QuoteCreateInput,
  type QuoteDetail,
  type QuoteDraft,
  type QuoteListRow,
} from '../../lib/quote-api';
import { QuoteFormModal } from './QuoteFormModal';
import { QuotePreviewModal } from './QuotePreviewModal';
import { LinkSalesModal } from './LinkSalesModal';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { mailSubject } from '../../lib/mail-subject';
import { LoadingLines } from '../../components/ui/Skeleton';

export function QuotesTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<QuoteListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filterCompany, setFilterCompany] = useState<'' | QuoteCompany>('');
  const [filterKind, setFilterKind] = useState<'' | QuoteKind>('');
  const [query, setQuery] = useState('');

  // 작성/수정 모달
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formInitial, setFormInitial] = useState<QuoteDetail | null>(null);
  // create 모드 미리채움 — 엑셀 업로드 인식 결과 또는 기존 견적서 복사
  const [prefillDraft, setPrefillDraft] = useState<QuoteDraft | null>(null);
  const [prefillWarnings, setPrefillWarnings] = useState<string[]>([]);
  const [prefillKind, setPrefillKind] = useState<'excel' | 'copy'>('excel');
  /** 복사 원본 견적번호 — 작성 폼 제목 옆 부제로 표시 */
  const [copySourceNo, setCopySourceNo] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 미리보기 모달
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewQuote, setPreviewQuote] = useState<QuoteDetail | null>(null);

  // 영업진행 연동 모달 (목록에서 직접 진입)
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkQuote, setLinkQuote] = useState<QuoteDetail | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setRows(await listQuotes());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const canModify = (row: QuoteListRow): boolean => {
    if (!user) return false;
    // 2026-05-29: admin/mgmt/sales 모두 타인 작성건 수정·삭제 가능 (backend 와 동일)
    if (user.role === 'admin' || user.role === 'mgmt' || user.role === 'sales') return true;
    if (user.userId === row.createdBy) return true;
    return false;
  };

  const filtered = rows.filter((r) => {
    if (filterCompany && r.companyId !== filterCompany) return false;
    if (filterKind && (r.quoteKind ?? '일반') !== filterKind) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      r.quoteNo.toLowerCase().includes(q) ||
      r.recipientCompany.toLowerCase().includes(q) ||
      r.projectName.toLowerCase().includes(q) ||
      r.empName.toLowerCase().includes(q) ||
      (r.firstItemName ?? '').toLowerCase().includes(q)
    );
  });

  /** 미리채움 상태 초기화 — 이전 모달 세션(엑셀/복사) 잔재 제거 */
  function clearPrefill() {
    setPrefillDraft(null);
    setPrefillWarnings([]);
    setPrefillKind('excel');
    setCopySourceNo('');
  }

  async function handleCreateClick() {
    setFormMode('create');
    setFormInitial(null);
    clearPrefill();
    setFormOpen(true);
  }

  async function handleUploadFile(file: File) {
    setUploading(true);
    try {
      const { draft, warnings } = await parseQuoteXlsx(file);
      setFormMode('create');
      setFormInitial(null);
      clearPrefill();
      setPrefillDraft(draft);
      setPrefillWarnings(warnings);
      setFormOpen(true);
    } catch (e) {
      alert('엑셀 인식 실패: ' + (e as Error).message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  /**
   * 기존 견적서 내용으로 새 견적서 작성 (2026-07-30).
   * 원본은 건드리지 않고, 같은 내용을 채운 '작성' 폼을 연다 → 저장 시 새 견적번호로 발번.
   */
  async function handleCopyClick(quoteId: number) {
    try {
      const detail = await getQuote(quoteId);
      setFormMode('create');
      setFormInitial(null);
      setPrefillDraft(quoteDetailToDraft(detail));
      setPrefillWarnings([]);
      setPrefillKind('copy');
      setCopySourceNo(detail.quoteNo);
      setPreviewOpen(false);
      setFormOpen(true);
    } catch (e) {
      alert('견적서 조회 실패: ' + (e as Error).message);
    }
  }

  async function handleEditClick(row: QuoteListRow) {
    try {
      const detail = await getQuote(row.id);
      setFormMode('edit');
      setFormInitial(detail);
      setFormOpen(true);
    } catch (e) {
      alert('견적서 조회 실패: ' + (e as Error).message);
    }
  }

  async function handleDeleteClick(row: QuoteListRow) {
    if (!confirm(`견적서 [${row.quoteNo}] 를 삭제하시겠습니까?\n( ${row.projectName || '제목 없음'} )`)) {
      return;
    }
    try {
      await deleteQuote(row.id);
      await reload();
    } catch (e) {
      alert('삭제 실패: ' + (e as Error).message);
    }
  }

  async function handlePreviewClick(row: QuoteListRow) {
    try {
      const detail = await getQuote(row.id);
      setPreviewQuote(detail);
      setPreviewOpen(true);
    } catch (e) {
      alert('미리보기 실패: ' + (e as Error).message);
    }
  }

  async function handleLinkClick(row: QuoteListRow) {
    try {
      const detail = await getQuote(row.id);
      setLinkQuote(detail);
      setLinkOpen(true);
    } catch (e) {
      alert('견적서 조회 실패: ' + (e as Error).message);
    }
  }

  async function handleFormSubmit(data: QuoteCreateInput) {
    if (formMode === 'create') {
      const res = await createQuote(data);
      setFormOpen(false);
      clearPrefill();
      await reload();
      // 작성 직후 미리보기로 이동
      const detail = await getQuote(res.id);
      setPreviewQuote(detail);
      setPreviewOpen(true);
    } else if (formMode === 'edit' && formInitial) {
      await updateQuote(formInitial.id, data);
      setFormOpen(false);
      await reload();
      // 수정 후에도 미리보기로 이동
      const detail = await getQuote(formInitial.id);
      setPreviewQuote(detail);
      setPreviewOpen(true);
    }
  }

  const canCreate = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5">
      {/* 헤더 (제목 + 필터 + 작성 버튼) */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="ui-section-title mr-auto">견적서 목록 ({filtered.length}건)</h2>
        <select
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value as '' | QuoteCompany)}
          className="ui-input"
        >
          <option value="">전체 사업자</option>
          <option value="SM">SM</option>
          <option value="EJ">EJ</option>
        </select>
        <select
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value as '' | QuoteKind)}
          className="ui-input"
        >
          <option value="">전체 종류</option>
          <option value="일반">일반</option>
          <option value="유지보수">유지보수</option>
        </select>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="번호/수신/프로젝트명/담당/품목 검색"
          className="ui-input w-64"
        />
        {canCreate && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUploadFile(f);
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="포털 견적 템플릿과 같은 서식의 엑셀을 올리면 인식해서 작성 폼에 채워줍니다"
              className="btn btn-success-solid"
            >
              {uploading ? '인식 중…' : '📥 엑셀 업로드'}
            </button>
            <button
              type="button"
              onClick={handleCreateClick}
              className="btn btn-primary"
            >
              + 견적서 작성
            </button>
          </>
        )}
      </div>

      {err && (
        <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">
          {err}
        </div>
      )}

      {/* 목록 표 */}
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              <Th>견적일자</Th>
              <Th>사업자</Th>
              <Th>견적번호</Th>
              <Th>담당</Th>
              <Th>수신</Th>
              <Th>프로젝트명</Th>
              <Th>상세내용</Th>
              <Th align="right">공급가 총계<br />(VAT별도)</Th>
              {/* 머리글을 2줄로 접어 칸 폭 요구를 86→56px 로 낮춘다 — [작업] 칸의 📋 버튼 자리를
                  여기서 내준다. 머리글 줄은 [공급가 총계] 때문에 이미 2줄이라 높이 비용 없음 (2026-07-30) */}
              <Th align="center">영업진행<br />연동</Th>
              <Th align="center">PDF</Th>
              <Th align="center">Excel</Th>
              <Th align="center">작업</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="text-center py-6 text-slate-400"><LoadingLines /></td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center py-6 text-slate-400">
                  견적서 없음
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => handlePreviewClick(r)}
                >
                  <Td>{r.quoteDate}</Td>
                  <Td>
                    <span
                      className={
                        'inline-block px-1.5 py-0.5 text-[11px] rounded font-medium ' +
                        (r.companyId === 'SM'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700')
                      }
                    >
                      {r.companyId}
                    </span>
                    {r.quoteKind === '유지보수' && (
                      <span
                        className="ml-1 inline-block px-1.5 py-0.5 text-[11px] rounded font-medium bg-teal-100 text-teal-700"
                        title="유지보수 견적서 — 품목별 기간(개월)·월 청구금액 양식"
                      >
                        유지보수
                      </span>
                    )}
                  </Td>
                  {/* 견적번호는 줄바꿈 금지 — [작업] 칸에 버튼이 늘면 이 칸이 먼저 눌려 2줄로 접혔다 (2026-07-30) */}
                  <Td className="font-mono text-xs whitespace-nowrap">
                    {r.quoteNo}
                    {/* 🧮 이어진 원가분석표 (v2.236.0) — 표에서 만든 견적서거나 견적서로 만든 표 */}
                    {r.costSheetId && (
                      <Link
                        to={costSheetUrl(r.costSheetId)}
                        onClick={(e) => e.stopPropagation()}
                        title="이 견적서와 이어진 원가분석표 열기 (가격표 ▸ 원가분석)"
                        className="ml-1 font-sans text-xs px-1 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-200"
                      >
                        🧮
                      </Link>
                    )}
                  </Td>
                  <Td>{r.empDisplay}</Td>
                  <Td>{r.recipientCompany}</Td>
                  <Td className="max-w-[20ch] truncate" title={r.projectName}>
                    {r.projectName}
                  </Td>
                  <Td className="max-w-[24ch] truncate" title={r.firstItemDescription || r.firstItemName}>
                    {r.firstItemName ? (
                      <>
                        {r.firstItemName}
                        {r.itemCount > 1 ? <span className="text-slate-500"> 외</span> : null}
                      </>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </Td>
                  <Td align="right" className="font-medium">
                    {r.totalSupply.toLocaleString()}
                  </Td>
                  <Td align="center" onClick={(e) => e.stopPropagation()}>
                    {/* 유지보수 견적서는 영업진행에 연동하지 않는다 (v2.104.0) */}
                    {canModify(r) && r.quoteKind !== '유지보수' ? (
                      <button
                        type="button"
                        onClick={() => handleLinkClick(r)}
                        title="영업진행 연동 (신규 등록 / 기존 갱신)"
                        className="btn btn-outline btn-sm"
                      >
                        🔗
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-500">-</span>
                    )}
                  </Td>
                  <Td align="center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openServerPdfPreview({
                          pdfUrl: quotePdfDownloadUrl(r.id),
                          filename: `견적서_${r.quoteNo}.pdf`,
                          mailDoc: {
                            docRefs: [
                              { docType: 'quote', docId: r.id, label: `견적서 ${r.quoteNo} (PDF)` },
                              { docType: 'quote-xlsx', docId: r.id, label: `견적서 ${r.quoteNo} (엑셀)` },
                            ],
                            subject: mailSubject('견적서 송부', r.projectName || r.quoteNo),
                          },
                        });
                      }}
                      className="btn btn-danger btn-sm"
                      title="견적서 미리보기"
                    >
                      📄
                    </button>
                  </Td>
                  <Td align="center" onClick={(e) => e.stopPropagation()}>
                    <a
                      href={quoteExcelDownloadUrl(r.id)}
                      className="inline-block px-2 py-0.5 text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded border border-emerald-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      📊
                    </a>
                  </Td>
                  {/* 버튼 3개를 한 줄로 유지 — 접히면 행 높이가 37→57px 로 늘어난다 (2026-07-30 실측) */}
                  <Td align="center" className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {canCreate && (
                      <button
                        type="button"
                        onClick={() => handleCopyClick(r.id)}
                        className="btn btn-outline btn-sm mr-0.5"
                        title="이 견적서 내용으로 새 견적서 작성 (원본은 그대로 남습니다)"
                      >
                        📋
                      </button>
                    )}
                    {canModify(r) ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleEditClick(r)}
                          className="btn btn-warning btn-sm mr-0.5"
                          title="수정"
                        >
                          ✏
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(r)}
                          className="btn btn-danger btn-sm"
                          title="삭제"
                        >
                          🗑
                        </button>
                      </>
                    ) : (
                      <span className="text-[11px] text-slate-500">조회만</span>
                    )}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <QuoteFormModal
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        prefill={prefillDraft}
        prefillWarnings={prefillWarnings}
        prefillKind={prefillKind}
        subtitle={prefillKind === 'copy' && copySourceNo ? `원본 ${copySourceNo} 복사` : undefined}
        onClose={() => {
          setFormOpen(false);
          clearPrefill();
        }}
        onSubmit={handleFormSubmit}
      />
      <QuotePreviewModal
        open={previewOpen}
        quote={previewQuote}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewQuote(null);
        }}
        onEdit={
          canCreate && previewQuote
            ? () => {
                setFormMode('edit');
                setFormInitial(previewQuote);
                setPreviewOpen(false);
                setFormOpen(true);
              }
            : undefined
        }
        onCopy={canCreate && previewQuote ? () => handleCopyClick(previewQuote.id) : undefined}
      />
      <LinkSalesModal
        open={linkOpen}
        quote={linkQuote}
        onClose={() => {
          setLinkOpen(false);
          setLinkQuote(null);
        }}
      />
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  const cls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`px-2 py-1.5 text-xs font-medium text-slate-600 whitespace-nowrap ${cls}`}>
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  className,
  title,
  onClick,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  title?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const cls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <td
      className={`px-2 py-1.5 text-sm text-slate-700 align-middle ${cls} ${className ?? ''}`}
      title={title}
      onClick={onClick}
    >
      {children}
    </td>
  );
}
